import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  IconButton, TablePagination, Tooltip, Menu, MenuItem,
  TextField, FormControl, InputLabel, Select, Divider, Button, Typography, Alert, CircularProgress,
  useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, MapPin } from 'lucide-react';
import axios from '../utils/axios';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions, DeleteConfirmDialog, FilterDrawer } from '../components/common/modal';
import EmptyState from '../components/common/EmptyState';
import LoadingState from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const CitiesView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    city_name: '',
    state_id: '',
    city_status: 'Activated'
  });

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionCity, setActionCity] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadCities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/admin/cities', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
          state_id: stateFilter || undefined,
          status: statusFilter || undefined
        }
      });
      setCities(response.data.cities || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, stateFilter, statusFilter]);

  const loadStates = async () => {
    try {
      const response = await axios.get('/state');
      setStates(response.data || []);
    } catch (err) {
      console.error('Failed to load states', err);
    }
  };

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  useEffect(() => {
    loadStates();
  }, []);

  const handleMenuOpen = (event, cityItem) => {
    setAnchorEl(event.currentTarget);
    setActionCity(cityItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionCity(null);
  };

  const handleOpenModal = (cityItem = null) => {
    if (cityItem) {
      setEditingCity(cityItem);
      setFormData({
        city_name: cityItem.city_name,
        state_id: cityItem.state_id?._id || cityItem.state_id || '',
        city_status: cityItem.city_status || 'Activated'
      });
    } else {
      setEditingCity(null);
      setFormData({
        city_name: '',
        state_id: states[0]?._id || '',
        city_status: 'Activated'
      });
    }
    setError(null);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (actionCity) {
      try {
        setLoading(true);
        await axios.delete(`/admin/cities/${actionCity._id}`);
        setDeleteDialogOpen(false);
        setActionCity(null);
        loadCities();
      } catch (err) {
        setError(err.response?.data?.msg || 'Failed to delete city');
        setDeleteDialogOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.city_name.trim() || !formData.state_id) return;

    try {
      setLoading(true);
      if (editingCity) {
        await axios.put(`/admin/cities/${editingCity._id}`, formData);
      } else {
        await axios.post('/admin/cities', formData);
      }
      setIsModalOpen(false);
      loadCities();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save city');
    } finally {
      setLoading(false);
    }
  };

  const activeFiltersCount = (stateFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const filterChips = [];
  if (stateFilter) {
    filterChips.push({
      key: 'state',
      label: `State: ${states.find((s) => s._id === stateFilter)?.state_name || stateFilter}`,
      onDelete: () => { setStateFilter(''); setPage(0); }
    });
  }
  if (statusFilter) {
    filterChips.push({
      key: 'status',
      label: `Status: ${statusFilter}`,
      onDelete: () => { setStatusFilter(''); setPage(0); }
    });
  }

  const handleResetFilters = () => {
    setStateFilter('');
    setStatusFilter('');
    setSearchTerm('');
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="Cities"
        subtitle="Manage municipal territories, states, and regional coverage"
        count={total}
        countLabel="cities"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Geography', href: '/countries' },
          { label: 'Cities' }
        ]}
        actionLabel="New City"
        actionIcon={<Plus size={18} />}
        onAction={() => handleOpenModal()}
      />

      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <FilterBar
          searchPlaceholder="Search city, state or country..."
          searchValue={searchTerm}
          onSearchChange={(val) => setSearchTerm(val)}
          activeFiltersCount={activeFiltersCount}
          filterChips={filterChips}
          onResetFilters={handleResetFilters}
          showFilterButton
          filterDrawerOpen={isFilterOpen}
          onToggleFilterDrawer={() => setIsFilterOpen(!isFilterOpen)}
          extraFilters={
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: '0.8125rem' }}>Filter by State</InputLabel>
              <Select
                value={stateFilter}
                label="Filter by State"
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: 2, fontSize: '0.8125rem' }}
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.state_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        >
          <ExportTableButton
            data={cities}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/admin/cities', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                  state_id: stateFilter || undefined,
                  status: statusFilter || undefined,
                },
              });
              return res.data.cities || [];
            }}
            columns={[
              { label: 'City Name', key: 'city_name' },
              { label: 'State', value: (row) => row.state_id?.state_name || 'N/A' },
              { label: 'Country', value: (row) => row.state_id?.country_id?.country_name || 'N/A' },
              { label: 'Status', key: 'city_status' },
            ]}
            filename="cities"
          />
        </FilterBar>

        {error && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Box>
        )}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>City Name</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && cities.length === 0 ? (
                <LoadingState rows={rowsPerPage} cols={5} />
              ) : cities.length > 0 ? (
                cities.map((c) => (
                  <TableRow key={c._id} hover>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {c.city_name}
                    </TableCell>
                    <TableCell>{c.state_id?.state_name || '—'}</TableCell>
                    <TableCell>{c.state_id?.country_id?.country_name || '—'}</TableCell>
                    <TableCell>
                      <StatusBadge status={c.city_status || 'Activated'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Actions">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, c)}>
                          <MoreVertical size={18} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={MapPin}
                      title="No cities found"
                      description={searchTerm || stateFilter || statusFilter ? "No cities matched your active search or filters. Try adjusting them." : "Start building your regional catalog by creating the first city."}
                      actionLabel={searchTerm || stateFilter || statusFilter ? "Clear Filters" : "New City"}
                      onAction={searchTerm || stateFilter || statusFilter ? handleResetFilters : () => handleOpenModal()}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 1.5 }, borderTop: 1, borderColor: 'divider' }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: { sx: { width: 140, p: 0.5 } }
        }}
      >
        <MenuItem onClick={() => handleOpenModal(actionCity)} sx={{ fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1.25, borderRadius: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal Dialog */}
      <AdminDialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="sm">
        <form onSubmit={handleFormSubmit}>
          <AdminDialogHeader
            title={editingCity ? 'Edit City' : 'New City'}
            subtitle="Manage municipal market and city location"
            icon={MapPin}
            onClose={() => setIsModalOpen(false)}
          />
          <AdminDialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 1 }}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>
              )}
              <TextField
                label="City Name"
                fullWidth
                required
                value={formData.city_name}
                onChange={(e) => setFormData({ ...formData, city_name: e.target.value })}
                placeholder="e.g. Mumbai, New York, Toronto"
              />

              <FormControl fullWidth required>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state_id}
                  label="State"
                  onChange={(e) => setFormData({ ...formData, state_id: e.target.value })}
                >
                  {states.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.state_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.city_status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, city_status: e.target.value })}
                >
                  <MenuItem value="Activated">Activated</MenuItem>
                  <MenuItem value="Deactivated">Deactivated</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </AdminDialogContent>
          <AdminDialogActions>
            <Button onClick={() => setIsModalOpen(false)} color="inherit" disabled={loading} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading && <CircularProgress size={16} color="inherit" />}
              sx={{ px: 3, fontWeight: 700 }}
            >
              {loading ? 'Saving...' : 'Save City'}
            </Button>
          </AdminDialogActions>
        </form>
      </AdminDialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        itemName={actionCity?.city_name}
        itemType="City"
        onConfirm={confirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
        loading={loading}
      />

      {/* Standardized Filter Drawer */}
      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filter Cities"
        activeCount={[stateFilter, statusFilter].filter(Boolean).length}
        width={380}
        onReset={() => {
          setStateFilter('');
          setStatusFilter('');
          setPage(0);
          setIsFilterOpen(false);
        }}
        onApply={() => setIsFilterOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* State Filter */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              STATE
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={stateFilter}
                displayEmpty
                onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value="">All States</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s._id} value={s._id}>{s.state_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Status Filter */}
          <Box>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.secondary', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              STATUS
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={statusFilter}
                displayEmpty
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{ borderRadius: 1.5 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Activated">Activated</MenuItem>
                <MenuItem value="Deactivated">Deactivated</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </FilterDrawer>
    </Box>
  );
};

export default CitiesView;

